'use es6';

import { Component } from 'react';
import I18n from 'I18n';
import { List, Range } from 'immutable';
import { trackCalendarInteraction } from 'ui-addon-calendars/lib/usageTracker';
import CALENDAR_VIEW_TYPES from 'ui-addon-calendars/constants/CalendarViewTypes';
import WeekCalendarView from './WeekCalendarView';
import { getSortedChildren, distributeEventsToWeekDays, distributeEventsToHours } from '../../lib/eventParse';
import { getWeekLowerBound, getWeekUpperBound, getDayOfWeek } from '../../lib/time';
import { minWeekEventWidth } from '../../constants/sizes';
import { CalendarContext } from '../CalendarContextProvider';
import { getEventsToDisplay } from '../../lib/eventDisplay';
import { WeekOverflow } from '../../components/Overflow/Overflow';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
const OverflowButton = ({
  children
}) => /*#__PURE__*/_jsx(View, {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  blockSize: "100%",
  children: children
});
export default class WeekCalendarWrapper extends Component {
  constructor(props) {
    super(props);
    this.state = {
      openPopover: null
    };
    this.registerDayRowRef = this.registerDayRowRef.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleOverflowClick = this.handleOverflowClick.bind(this);
    this.handleOverflowClose = this.handleOverflowClose.bind(this);
  }
  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }
  handleResize() {
    if (!this._dayRowRef) {
      return;
    }
    // lb: is this confusing height and width?
    const currentHeight = this._dayRowRef.clientWidth;
    const maxEventDisplayCount = Math.floor(currentHeight / minWeekEventWidth);
    if (maxEventDisplayCount !== this.previousDisplayCount) {
      this.setState({
        maxEventDisplayCount
      });
    }
    this.previousDisplayCount = maxEventDisplayCount;
  }
  registerDayRowRef(dayRowRef) {
    this._dayRowRef = dayRowRef;
    if (!this.previousDisplayCount) {
      this.handleResize();
    }
  }
  getEventsForDay(events, dayOfWeek) {
    const {
      timezone
    } = this.props;
    return events.filter(event => {
      const {
        start
      } = event.props;
      return getDayOfWeek(start, timezone) === dayOfWeek;
    });
  }
  getWeekEventRange() {
    const {
      displayDay,
      displayYear,
      timezone,
      hideWeekends
    } = this.props;
    const start = getWeekLowerBound(displayYear, displayDay, hideWeekends, timezone, I18n.moment.locale()).valueOf();
    const end = getWeekUpperBound(displayYear, displayDay, hideWeekends, timezone, I18n.moment.locale()).valueOf();
    return {
      start,
      end
    };
  }
  getDistributedAllDayEvents(events) {
    const {
      timezone,
      hideWeekends
    } = this.props;
    const {
      start: weekStartDay,
      end: weekEndDay
    } = this.getWeekEventRange();
    const distributedEvents = distributeEventsToWeekDays(events, weekStartDay, weekEndDay, timezone, hideWeekends);
    const asArray = Object.keys(distributedEvents).map(key => distributedEvents[key]);
    return List(asArray);
  }
  getMaxAllDayEventsIndex(distributedEvents) {
    return distributedEvents.max((listA, listB) => {
      return listA.size - listB.size;
    }).size;
  }
  getEventsDistributedByDayAndHour(events) {
    const {
      timezone
    } = this.props;
    return Range(0, 7).map(dayOfWeek => {
      return distributeEventsToHours(this.getEventsForDay(events, dayOfWeek), timezone);
    }).toList();
  }
  handleOverflowClose() {
    this.setState({
      openPopover: null
    });
  }
  handleOverflowClick(popoverKey) {
    const {
      closeCurrentPopover
    } = this.context;
    trackCalendarInteraction({
      action: 'click view more',
      viewType: CALENDAR_VIEW_TYPES.WEEK,
      total: popoverKey.overflowCount
    });
    this.setState({
      openPopover: popoverKey
    });
    closeCurrentPopover();
  }
  render() {
    const {
      children,
      overrideMaxEventDisplayCount,
      useExactEventTimes,
      timezone,
      eventDisplay
    } = this.props;
    const {
      maxEventDisplayCount,
      openPopover
    } = this.state;
    const sortedChildren = List(getSortedChildren(children, useExactEventTimes, timezone));
    const eventsToDisplay = getEventsToDisplay({
      sortedChildren,
      eventDisplay,
      eventRange: this.getWeekEventRange()
    });
    const distributedAllDayEvents = this.getDistributedAllDayEvents(eventsToDisplay.allDayEvents);
    const maxAllDayEventsIndex = this.getMaxAllDayEventsIndex(distributedAllDayEvents);
    const distributedEvents = this.getEventsDistributedByDayAndHour(eventsToDisplay.events);
    return /*#__PURE__*/_jsx(WeekCalendarView, Object.assign({}, this.props, {
      dayRowRef: this.registerDayRowRef,
      allDayEvents: distributedAllDayEvents,
      events: distributedEvents,
      maxAllDayEventsIndex: maxAllDayEventsIndex,
      maxEventDisplayCount: overrideMaxEventDisplayCount || maxEventDisplayCount,
      onDndDrop: this.props.onDndDrop,
      onDndHover: this.props.onDndHover,
      canDrop: this.props.canDrop,
      openPopover: openPopover,
      onPopoverClose: this.handleOverflowClose,
      renderOverflow: ({
        actualDate,
        hour,
        dayOfWeek,
        dayIndex
      }) => /*#__PURE__*/_jsx(WeekOverflow, Object.assign({}, this.props, {
        openPopover: openPopover,
        onPopoverClose: this.handleOverflowClose,
        actualDate: actualDate,
        events: distributedEvents,
        dayOfWeek: dayOfWeek,
        dayIndex: dayIndex,
        hour: hour,
        onOverflowButtonClick: this.handleOverflowClick,
        buttonAs: OverflowButton,
        maxEventDisplayCount: overrideMaxEventDisplayCount || maxEventDisplayCount
      }))
    }));
  }
}
WeekCalendarWrapper.contextType = CalendarContext;
WeekCalendarWrapper.defaultProps = {
  displayDay: 1
};